import { zodResolver } from "@hookform/resolvers/zod";
import { NoAccessCard } from "@impero-com/common/components/noAccessCard/index";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { emitNotification, Button } from "@impero-com/ui";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { OrgApplicationBar } from "components/applicationBar/orgApplicationBar";
import { StickyFooter } from "components/stickyFooter/footer";
import { FormFooterWrapper } from "components/stickyFooter/FormFooterWrapper";
import { ViewCancelButton } from "components/stickyFooter/ViewCancelButton";
import { useCreateEntityMutation } from "org/entities/queries/useCreateEntityMutation";
import { WebRoute } from "routes";
import { useCurrentUserOverview } from "utils/hooks/useCurrentUserOverview";
import { useUserStatusQuery } from "utils/queries/useUserStatusQuery";

import { useEntitiesBreadCrumbs } from "../useEntitiesBreadCrumbs";

import { ControlEntitiesForm } from "./ControlEntitiesForm";
import {
    ControlEntityFormSchema,
    useControlEntityViewFormSchema,
} from "./useControlEntityViewFormSchema";

export function ControlEntitiesCreatePage() {
    const { t } = useTranslation();
    const { data: userStatus } = useUserStatusQuery();
    const userHasAccess = userStatus.actions.controlEntities.create;
    const breadCrumbs = useEntitiesBreadCrumbs();

    return (
        <OrgApplicationBar
            title={{
                title: t("Entities_CreateNewEntity"),
                breadCrumbs,
            }}
        >
            {userHasAccess ? <View /> : <NoAccessCard />}
        </OrgApplicationBar>
    );
}
function View() {
    const { t } = useTranslation();

    const schema = useControlEntityViewFormSchema();
    const currentUser = useCurrentUserOverview();
    const { mutateAsync, isPending } = useCreateEntityMutation();
    const navigate = useNavigate();

    const { control, formState, handleSubmit } =
        useForm<ControlEntityFormSchema>({
            resolver: zodResolver(schema),
            mode: "onBlur",
            defaultValues: {
                name: "",
                description: "",
                responsibleId: currentUser?.id,
                kind: "Control",
                controlUserIdAndRights: [],
                riskUserIdAndRights: [],
                tagIds: [],
            },
        });

    const handleSubmitClick = handleSubmit(
        async (data) => {
            await mutateAsync({
                data: {
                    name: data.name,
                    description: data.description,
                    responsibleId: data.responsibleId,
                    kind: data.kind,
                    controlUserIdAndRights: data.controlUserIdAndRights,
                    riskUserIdAndRights: data.riskUserIdAndRights,
                    tagIds: data.tagIds,
                },
            });
            navigate(WebRoute.ENTITIES);
            emitNotification("success", t("Entity_Create_Success"));
        },
        (error) => {
            console.error(error);
            emitNotification("error", t("Entities_SubmitValidationError"));
        }
    );
    if (!currentUser) {
        return null;
    }
    return (
        <FormFooterWrapper>
            <ControlEntitiesForm
                control={control}
                users={[currentUser]}
                canEdit
                canEditControlAccessRights
                canEditRiskAccessRights
            />
            <StickyFooter>
                <Button
                    size="large"
                    variant="primary"
                    disabled={!formState.isDirty}
                    loading={isPending}
                    onClick={() => void handleSubmitClick()}
                >
                    {t("Entities_Create_Title")}
                </Button>
                <ViewCancelButton
                    isDirty={formState.isDirty}
                    bodyLabel={t("Entities_ConfirmCancel_Body")}
                />
            </StickyFooter>
        </FormFooterWrapper>
    );
}
